/**
 * The section crumbs, declared once.
 *
 * A breadcrumb that says one thing pointing at a screen titled another is the
 * kind of drift six developers produce in a fortnight, so the label and the
 * destination are defined together here and every trail is assembled from these.
 *
 * **Each label must match the `PageHeader` title of the screen it points at.** If
 * a screen is renamed, this file is the other half of that change.
 *
 * `programs` is the one exception, and a deliberate one: the crumb was changed to
 * the sidebar's "Programs" on request while the screen is still headed
 * "Programs". Do not quietly revert it to match the title - renaming the screen
 * is the other half of that change, and it has not been asked for yet.
 */

import type { Crumb } from '../components/common';

export const CRUMB = {
  volunteers: { label: 'Volunteers', to: '/volunteers' },
  /** Not in the sidebar (Plan D12) — reached from the volunteers screen. */
  registrations: { label: 'All submissions', to: '/registrations' },
  programs: { label: 'Programs', to: '/programs' },
  activities: { label: 'Activities', to: '/activities' },
  attendance: { label: 'Attendance', to: '/attendance' },
} as const satisfies Record<string, Crumb>;

/**
 * Which list a detail screen was opened from, carried in router state.
 *
 * An activity sits under a program *and* under the flat Activities list, and
 * before this the trail always said "program" — so opening one from Activities
 * gave a way back that skipped the stage the person had actually come through
 * and dropped them into Programs instead (Parinaam, 13 Sep 2026).
 *
 * A detail screen cannot work this out for itself: the URL is the same either
 * way. So the list that opened it says so, in `navigate(..., { state })`, which
 * survives a reload because it lives in `window.history.state`. **Absence is
 * meaningful and must stay handled** — a pasted link, a bookmark, or a link from
 * some third screen arrives with no state at all, and those fall back to the
 * structural trail, which is still the truest answer when nobody knows where the
 * visitor came from.
 */
export interface CameFrom {
  from?: 'activities' | 'program';
}

/** Read the origin out of `useLocation().state`, tolerating anything else there. */
export function cameFrom(state: unknown): CameFrom['from'] | undefined {
  const from = (state as CameFrom | null)?.from;
  return from === 'activities' || from === 'program' ? from : undefined;
}
